import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { absoluteUrl, canonicalPath, SITE_NAME } from '../config/site'
import pageMetadata from '../data/seo-pages.json'
import { contact, getHorseBySlug, socialLinks } from '../data/site'
import PageMetadata from './PageMetadata'

type PageEntry = {
  path: string
  title: string
  description: string
  image: string
}

const pages = new Map((pageMetadata as PageEntry[]).map((page) => [page.path, page]))
const organizationId = `${absoluteUrl('/')}#organization`
const websiteId = `${absoluteUrl('/')}#website`

const organizationSchema = {
  '@type': 'Organization',
  '@id': organizationId,
  name: SITE_NAME,
  url: absoluteUrl('/'),
  logo: {
    '@type': 'ImageObject',
    url: absoluteUrl('/images/udhs-logo.webp'),
    width: 2560,
    height: 732,
  },
  description: 'An Ocala, Florida horse rescue and sanctuary providing rescued horses and ponies with rehabilitation, dependable daily care, and long-term sanctuary.',
  telephone: contact.phone,
  email: contact.emails[0],
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1026 SW 9th St, Suite D',
    addressLocality: 'Ocala',
    addressRegion: 'FL',
    postalCode: '34471',
    addressCountry: 'US',
  },
  sameAs: Object.values(socialLinks),
}

const websiteSchema = {
  '@type': 'WebSite',
  '@id': websiteId,
  url: absoluteUrl('/'),
  name: SITE_NAME,
  publisher: { '@id': organizationId },
  inLanguage: 'en-US',
}

export default function SiteMetadata() {
  const { pathname } = useLocation()
  const path = canonicalPath(pathname)
  const horseSlug = path.match(/^\/horses\/([^/]+)$/)?.[1]
  const horse = horseSlug ? getHorseBySlug(decodeURIComponent(horseSlug)) : undefined
  const page = pages.get(path)
  const isNotFound = !page && !horse

  const metadata = horse
    ? {
        title: `Meet ${horse.name} | Unique Diamond Horse Sanctuary`,
        description: `Meet ${horse.name} at Unique Diamond Horse Sanctuary. ${horse.summary}`,
        image: horse.photos[0].src,
        imageAlt: horse.photos[0].alt,
      }
    : page
      ? { ...page, imageAlt: `${SITE_NAME} in Ocala, Florida` }
      : {
          title: `Page Not Found | ${SITE_NAME}`,
          description: `The requested page was not found. Return to ${SITE_NAME} to meet the rescued herd and explore the sanctuary.`,
          image: '/images/about-banner.webp',
          imageAlt: `${SITE_NAME} rescued horses`,
        }

  const canonicalUrl = isNotFound ? undefined : absoluteUrl(path)
  const imageUrl = absoluteUrl(metadata.image)

  const structuredData = useMemo(() => {
    if (isNotFound || !canonicalUrl) return []

    const webpageId = `${canonicalUrl}#webpage`
    const graph: Record<string, unknown>[] = [
      organizationSchema,
      websiteSchema,
      {
        '@type': 'WebPage',
        '@id': webpageId,
        url: canonicalUrl,
        name: metadata.title,
        description: metadata.description,
        isPartOf: { '@id': websiteId },
        about: horse ? { '@id': `${canonicalUrl}#resident` } : { '@id': organizationId },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: imageUrl,
          caption: metadata.imageAlt,
        },
        inLanguage: 'en-US',
      },
    ]

    if (horse) {
      graph.push(
        {
          '@type': 'Thing',
          '@id': `${canonicalUrl}#resident`,
          name: horse.profileName || horse.name,
          alternateName: horse.profileName && horse.profileName !== horse.name ? horse.name : undefined,
          description: horse.summary,
          image: horse.photos.map((photo) => absoluteUrl(photo.src)),
          mainEntityOfPage: { '@id': webpageId },
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${canonicalUrl}#breadcrumbs`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrl('/') },
            { '@type': 'ListItem', position: 2, name: 'Meet the Horses', item: absoluteUrl('/horses') },
            { '@type': 'ListItem', position: 3, name: horse.name, item: canonicalUrl },
          ],
        },
      )
    }

    return graph
  }, [canonicalUrl, horse, imageUrl, isNotFound, metadata.description, metadata.imageAlt, metadata.title])

  return (
    <PageMetadata
      title={metadata.title}
      description={metadata.description}
      image={imageUrl}
      imageAlt={metadata.imageAlt}
      url={canonicalUrl}
      robots={isNotFound ? 'noindex, nofollow' : undefined}
      structuredData={structuredData}
    />
  )
}
